/**
 * ═══ ONE READ OUT AT A TIME FOR THE SAME ADDRESS (#1917) ═══
 *
 * Staging 0.2.477, counted in the browser: an open asked /api/earnings three times, /api/me, /api/agents and the
 * cash read twice each, while the first was still out; on 4536 (8 Oct) a coin page asked its type tag and the
 * watchlist 23 times each in 12 s, one per repaint while the first answer was out. Each asker is a different screen
 * part with its own reason to ask, so the fix is here, under all of them: a plain read of the same address, as the same
 * person and agent, asked while the same one is out, waits for that one and gets its own copy of the answer.
 *
 * Only plain reads: GET, a string or URL address under /api/, no body, no own cache rule, no stop of its own (a read
 * with a stop may join one already out, but is never joined, so a stop never ends someone else's read), and never the
 * live stream. A read asked after the first one has answered goes out as before: this shares, it never keeps.
 *
 * Installed first, under every other wrapper of fetch (the agent header is added above it), so the address, the
 * sign-in and the agent of the read that leaves are what is compared.
 */
export function installSharedReads(): void {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  const real = window.fetch.bind(window);
  const out = new Map<string, Array<{ resolve: (res: Response) => void; reject: (why: unknown) => void }>>();
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const key = keyOf(input, init);
    if (key === null) return real(input, init);
    const waiting = out.get(key);
    if (waiting) return new Promise<Response>((resolve, reject) => { waiting.push({ resolve, reject }); });
    /** A read with its own stop is not joined (see above). */
    if (init?.signal) return real(input, init);
    const joined: Array<{ resolve: (res: Response) => void; reject: (why: unknown) => void }> = [];
    out.set(key, joined);
    const asked = real(input, init);
    /** Runs before the first asker's own handler (added first), so each copy is taken before anyone reads the body. */
    asked.then((res) => {
      out.delete(key);
      for (const one of joined) {
        try { one.resolve(res.clone()); } catch (why) { one.reject(why); }
      }
    }, (why: unknown) => {
      out.delete(key);
      for (const one of joined) one.reject(why);
    });
    return asked;
  };
}

function keyOf(input: RequestInfo | URL, init?: RequestInit): string | null {
  if (typeof input !== "string" && !(input instanceof URL)) return null;
  if ((init?.method ?? "GET").toUpperCase() !== "GET" || init?.body != null || init?.cache !== undefined || init?.keepalive) return null;
  let url: URL;
  try { url = new URL(String(input), location.href); } catch { return null; }
  if (url.origin !== location.origin || !url.pathname.startsWith("/api/") || /\/stream$/.test(url.pathname)) return null;
  const headers = new Headers(init?.headers);
  return [url.pathname + url.search, headers.get("authorization") ?? "", headers.get("x-genie-agent") ?? "", headers.get("accept") ?? ""].join("\n");
}
